/**
 * Expands / flattens `type: 'list'` fields to indexed top-level keys:
 *   `${listKey}${index}${rowFieldKey}`  (index is 1-based)
 *
 * List UI and flat-storage options live on the field under `listConfig`.
 * Flat indexed storage is the default (opt out with `listConfig.flatStorage: false`).
 * Optional listConfig keys:
 *   title, addButtonText, maxItems, rowFields, addRowConstraint
 *   reorderable — show up/down arrows to move rows
 *   flatFixedSlots — default on; opt out with `flatFixedSlots: false`
 *   flatStopAtEmptyRowKey — on expand, stop at first empty row field (e.g. 'Id')
 *   flatClearValues — per rowField default when clearing a slot (e.g. { Type: 'NONE' })
 *   flatEmptyValues — rowField values treated as empty when expanding
 *
 * Trailing slots beyond the current row count are removed via REMOVE_ATTRS on submit
 * so the backend drops stale indexed keys after row deletion.
 */

function newImportRowId(listKey, index) {
  return `lr_${listKey}_${index}_${Date.now().toString(36)}`
}

export function getListConfig(field) {
  return (field && field.listConfig) || {}
}

export function usesFlatListStorage(listField) {
  const cfg = getListConfig(listField)
  return !!(listField && cfg.flatStorage !== false)
}

export function usesFlatFixedSlots(listField) {
  const cfg = getListConfig(listField)
  return cfg.flatFixedSlots !== false
}

export function flatListStorageKey(listKey, index, rowFieldKey) {
  return `${listKey}${index}${rowFieldKey}`
}

function rowFieldKeys(listField) {
  const cfg = getListConfig(listField)
  const keys = (cfg.rowFields || []).map(rf => rf && rf.key).filter(Boolean)
  return [...new Set(keys)]
}

function appendRemoveAttr(target, attrKey) {
  if (!target.REMOVE_ATTRS) {
    target.REMOVE_ATTRS = []
  }
  if (!target.REMOVE_ATTRS.includes(attrKey)) {
    target.REMOVE_ATTRS.push(attrKey)
  }
  delete target[attrKey]
}

function removeTrailingFlatSlots(target, listField, fromIndex, toIndex) {
  if (!target || fromIndex > toIndex) {
    return
  }
  for (let index = fromIndex; index <= toIndex; index++) {
    for (const rfKey of rowFieldKeys(listField)) {
      appendRemoveAttr(target, flatListStorageKey(listField.key, index, rfKey))
    }
  }
}

function isEmptyForExpand(rowFieldKey, value, listField) {
  if (value === undefined || value === null || value === '') {
    return true
  }
  const cfg = getListConfig(listField)
  const emptyVals = cfg.flatEmptyValues || {}
  if (Object.prototype.hasOwnProperty.call(emptyVals, rowFieldKey)) {
    return String(value) === String(emptyVals[rowFieldKey])
  }
  return false
}

function slotHasContent(values, listField, index) {
  const listKey = listField.key
  for (const rfKey of rowFieldKeys(listField)) {
    const raw = values[flatListStorageKey(listKey, index, rfKey)]
    if (!isEmptyForExpand(rfKey, raw, listField)) {
      return true
    }
  }
  return false
}

function readFlatSlot(values, listField, index) {
  const listKey = listField.key
  const row = { id: newImportRowId(listKey, index) }
  for (const rfKey of rowFieldKeys(listField)) {
    const raw = values[flatListStorageKey(listKey, index, rfKey)]
    row[rfKey] = raw === undefined || raw === null ? '' : raw
  }
  return row
}

export function expandListFromFlatValues(values, listField) {
  if (!listField || !usesFlatListStorage(listField) || !values) {
    return []
  }
  if (Array.isArray(values[listField.key])) {
    return values[listField.key]
  }

  const cfg = getListConfig(listField)
  const maxItems = cfg.maxItems != null ? cfg.maxItems : 100
  const stopKey = cfg.flatStopAtEmptyRowKey
  const rows = []

  for (let index = 1; index <= maxItems; index++) {
    if (stopKey) {
      const stopVal = values[flatListStorageKey(listField.key, index, stopKey)]
      if (isEmptyForExpand(stopKey, stopVal, listField)) {
        break
      }
      rows.push(readFlatSlot(values, listField, index))
      continue
    }
    if (slotHasContent(values, listField, index)) {
      rows.push(readFlatSlot(values, listField, index))
    }
  }

  return rows
}

export function clearFlatListSlots(target, listField) {
  if (!listField || !usesFlatListStorage(listField) || !target) {
    return
  }
  const cfg = getListConfig(listField)
  const maxItems = cfg.maxItems != null ? cfg.maxItems : 0
  if (maxItems > 0) {
    removeTrailingFlatSlots(target, listField, 1, maxItems)
  }
}

export function flattenListToFlatValues(target, rows, listField) {
  if (!listField || !usesFlatListStorage(listField) || !target) {
    return
  }
  const cfg = getListConfig(listField)
  const listRows = Array.isArray(rows) ? rows : []
  const maxItems = cfg.maxItems != null ? cfg.maxItems : listRows.length

  for (let index = 1; index <= listRows.length; index++) {
    const row = listRows[index - 1]
    for (const rfKey of rowFieldKeys(listField)) {
      const flatKey = flatListStorageKey(listField.key, index, rfKey)
      const val = row[rfKey]
      target[flatKey] = val === undefined || val === null ? '' : val
    }
  }

  if (maxItems > listRows.length) {
    removeTrailingFlatSlots(target, listField, listRows.length + 1, maxItems)
  }
}

export function applyFlatListFieldsToFormData(target, fields, state, isFieldActive) {
  if (!target || !Array.isArray(fields)) {
    return
  }
  for (const field of fields) {
    if (!field || field.type !== 'list' || !usesFlatListStorage(field)) {
      continue
    }
    delete target[field.key]
    const active = typeof isFieldActive === 'function' ? isFieldActive(field) : true
    if (active) {
      flattenListToFlatValues(target, state[field.key] || [], field)
    } else {
      clearFlatListSlots(target, field)
    }
  }
}
